import { DEFAULT_FIAT_CURRENCY_DECIMALS } from '@frontend/common/constants/currency';
import { isEqualsIgnoreCase } from '@frontend/utils/string';
import { format } from 'date-fns';
import type { Address, Chain } from 'viem';
import * as chains from 'viem/chains';
import {
  CURRENCY_TO_SYMBOL,
  FIAT_PRECISION_DECIMALS,
  MAX_RAW_BALANCE,
  MIN_RAW_BALANCE,
  NATIVE_TOKEN_ADDRESS,
  TOKEN_DECIMALS,
} from '../constants';
import { Decimal } from '../lib';
import type { FormatAmountParams } from '../types';

export const MaxUint256 = BigInt(2 ** 256) - BigInt(1);

export function validateEmail(email: unknown): email is string {
  return typeof email === 'string' && email.length > 3 && email.includes('@');
}

// TODO:
// 0.003 => 0.0030
export function dnformat({
  value,
  decimals,
}: {
  value: bigint;
  decimals: number;
}) {
  if (!value) return undefined;
  const dustDigits = 2;

  // Use Decimal for precise division and rounding
  const decimalValue = new Decimal(value.toString());
  const divisor = new Decimal(10).pow(decimals);
  const divided = decimalValue.div(divisor);

  // Round down to dustDigits decimal places
  const roundedDown = divided.toDecimalPlaces(dustDigits, Decimal.ROUND_DOWN);

  return roundedDown.toString();
}

export const isValidNumber = (value?: string | null | number): boolean => {
  if (!value) return false;
  const num = Number(value);

  return !isNaN(num) && value.toString().trim() !== '';
};

/**
 * Truncates an ethereum address to the format 0x0000…0000
 * @param address Full address to truncate
 * @param size How many digits to keep
 * @returns Truncated address
 */
export const truncateAddress = (address: string, size: number) => {
  // kinda stupid but works
  const truncateRegex = {
    1: /^(0x[a-zA-Z0-9]{1})[a-zA-Z0-9]+([a-zA-Z0-9]{1})$/,
    2: /^(0x[a-zA-Z0-9]{2})[a-zA-Z0-9]+([a-zA-Z0-9]{2})$/,
    3: /^(0x[a-zA-Z0-9]{3})[a-zA-Z0-9]+([a-zA-Z0-9]{3})$/,
    4: /^(0x[a-zA-Z0-9]{4})[a-zA-Z0-9]+([a-zA-Z0-9]{4})$/,
  };
  const match = address.match(truncateRegex[size]);
  if (!match) return address;
  return `${match[1]}…${match[2]}`;
};

export const truncateTxHash = (
  hash: string,
  start: number = 8,
  end: number = 0,
): string => {
  if (hash.length <= 2 * start) {
    return hash;
  }

  if (end == 0) {
    return `${hash.slice(0, start)}...`;
  } else {
    return `${hash.slice(0, start)}...${hash.slice(-end)}`;
  }
};

export const formatExactTimestamp = (timestamp: Date | string) => {
  return format(new Date(new Date(timestamp)), 'dd-MM-yyyy HH:mm');
};

export const getViemChain = (id: number): Chain | undefined => {
  return Object.values(chains).find((x) => x.id === id);
};

// In our tokenlist we will always represent the native token with the native token address
// If the token address matches the native token address, is a native token
export const isNativeToken = (tokenAddress: Address) =>
  isEqualsIgnoreCase(tokenAddress, NATIVE_TOKEN_ADDRESS);

// If the token has a currency, it is a stablecoin
export const isStableCoin = (tokenCurrency: string | undefined) => {
  return !!tokenCurrency;
};

const getFractionDigits = (amount: number) => {
  const decimals = amount.toString().split('.')[1];
  return decimals ? decimals.replace(/0+$/, '').length : 0;
};

const formatCurrencySymbol = (
  amount: string,
  symbol = '',
  prefix = '',
  isFiatOrStablecoin = true,
) => {
  if (isFiatOrStablecoin) {
    return `${prefix}${symbol}${amount}`;
  }
  return `${prefix}${amount}${symbol ? ` ${symbol}` : ''}`;
};

export const formatBalanceAmount = ({
  amount,
  currency = '',
  symbol = currency,
  isFiatOrStablecoin = true,
  converted = false,
  shouldFormatCurrency = true,
}: FormatAmountParams) => {
  const currencySymbol =
    CURRENCY_TO_SYMBOL[currency as keyof typeof CURRENCY_TO_SYMBOL]?.symbol ||
    symbol ||
    '';

  const flooredAmount =
    Math.floor(amount * (isFiatOrStablecoin ? 100 : 10)) /
    (isFiatOrStablecoin ? 100 : 10);

  const fractionDigits = isFiatOrStablecoin
    ? 2
    : getFractionDigits(flooredAmount);

  const formatter = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  if (flooredAmount < MIN_RAW_BALANCE) {
    return shouldFormatCurrency
      ? formatCurrencySymbol(`0.01`, currencySymbol, '<', isFiatOrStablecoin)
      : '0.01';
  }

  if (flooredAmount >= MAX_RAW_BALANCE) {
    const compactFormatter = new Intl.NumberFormat('en-GB', {
      notation: 'compact',
      compactDisplay: 'short',
    });
    return shouldFormatCurrency
      ? formatCurrencySymbol(
          compactFormatter.format(flooredAmount),
          currencySymbol,
          '~',
          isFiatOrStablecoin,
        )
      : compactFormatter.format(flooredAmount);
  }

  return shouldFormatCurrency
    ? formatCurrencySymbol(
        formatter.format(flooredAmount),
        currencySymbol,
        converted ? '~' : '',
        isFiatOrStablecoin,
      )
    : formatter.format(flooredAmount);
};

export const formatPaymentAmount = ({
  amount,
  currency = '',
  symbol = currency,
  isFiatOrStablecoin = true,
  shouldFormatCurrency = true,
  useHigherPrecisionForSmallAmounts = false,
  precision = isFiatOrStablecoin
    ? DEFAULT_FIAT_CURRENCY_DECIMALS
    : TOKEN_DECIMALS,
}: FormatAmountParams): string => {
  const currencySymbol =
    CURRENCY_TO_SYMBOL[currency as keyof typeof CURRENCY_TO_SYMBOL]?.symbol ||
    symbol ||
    '';

  const smallAmountThreshold = Math.pow(10, -DEFAULT_FIAT_CURRENCY_DECIMALS);

  const shouldUseHigherPrecision =
    amount < smallAmountThreshold &&
    useHigherPrecisionForSmallAmounts &&
    isFiatOrStablecoin;

  const finalPrecision = shouldUseHigherPrecision
    ? FIAT_PRECISION_DECIMALS
    : precision;

  const scaleFactor = Math.pow(10, finalPrecision);
  const roundedAmount = Math.round(amount * scaleFactor) / scaleFactor;

  const fractionDigits = isFiatOrStablecoin
    ? shouldUseHigherPrecision
      ? Math.min(getFractionDigits(roundedAmount), FIAT_PRECISION_DECIMALS)
      : finalPrecision
    : Math.min(getFractionDigits(roundedAmount), finalPrecision);

  const formatter = new Intl.NumberFormat('en-GB', {
    minimumFractionDigits:
      isFiatOrStablecoin && !shouldUseHigherPrecision
        ? DEFAULT_FIAT_CURRENCY_DECIMALS
        : fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

  return shouldFormatCurrency
    ? formatCurrencySymbol(
        formatter.format(roundedAmount),
        currencySymbol,
        '',
        isFiatOrStablecoin,
      )
    : formatter.format(roundedAmount);
};

export const normalizeBigInt = (amount: bigint, decimals: number) => {
  const divisor = 10 ** decimals;

  return Number(amount) / divisor;
};

export const parseConfigString = (configString: string) => {
  const params = new URLSearchParams(configString);
  const tokens = params.get('tokens') ? params.get('tokens')?.split(',') : [];
  const chains = params.get('chains') ? params.get('chains')?.split(',') : [];
  return {
    tokens: tokens,
    chains: chains,
    currency: params.get('currency') ?? undefined,
    avatar: params.get('avatar') ?? undefined,
  };
};
